import {put, select, takeLatest} from 'redux-saga/effects';
import * as actions from '../../actions';
import axios, {AxiosResponse} from 'axios';
import {ActionType} from "typesafe-actions";
import {ExchangeTokenWithInternalAdapterResponse, TenantCreationSuccessResponse} from "../../actions/aiAgent/payloads";
import Config from '../../../config.json';
import {APIErrorInterface, ReduxState} from "../../types";
import {AiAgentsResponse, SsoResponse, TenantResponse} from "../../types/AiAgent";
import {TimeZone} from "../../types/TimeZone";


export function* exchangeTokenWithInternalAdapter(
    action: ActionType<typeof actions.exchangeTokenWithInternalAdapter.request>,
) {
    try {


        const response: AxiosResponse<ExchangeTokenWithInternalAdapterResponse> = yield axios.post(
            Config.VOICE_AI_ADAPTER_URL + '/auth/login',
            {
                portabilling_session_token: action.payload.portaBillingSessionToken,
            },
            {
                headers: {
                    'accept': 'application/json',
                    'Content-Type': 'application/json',
                },
            }
        );

        window.localStorage.setItem('internal_adapter_token', response.data.access_token);

        yield put(actions.exchangeTokenWithInternalAdapter.success({
            ...response.data,
        }));
    } catch (err: any) {

        const apiError: APIErrorInterface | undefined = err?.response?.data;
        yield put(actions.exchangeTokenWithInternalAdapter.failure(apiError));
    }
}

// Helper function to create tenant payload
function* createTenantPayload() {
    const { email } = yield select((state: ReduxState) => state.generic.sessionData);
    const customerEmail: string | null = yield select(
        (state: ReduxState) => state.generic.globalCustomerInfo?.customer_info?.email || null
    );
    const { customer_info } = yield select((state: ReduxState) => state.generic.globalCustomerInfo);
    const { timeZonesList } = yield select((state: ReduxState) => state.generic);

    return {
        email: customerEmail || email,
        customer_number: customer_info.i_customer.toString(),
        name: customer_info.name,
        language: customer_info.i_lang,
        currency: customer_info.iso_4217,
        country: customer_info.country,
        timezone: timeZonesList.find(
            (el: TimeZone) => el.i_time_zone === customer_info.i_time_zone
        )?.time_zone_name,
    };
}

// Helper function to create a new tenant
function* createTenant() {
    const { token } = yield select((state: ReduxState) => state.aiAgent);
    const tenantPayload = yield* createTenantPayload();

    const response: AxiosResponse<TenantCreationSuccessResponse> = yield axios.post(
        `${Config.VOICE_AI_ADAPTER_URL}/tenants`,
        tenantPayload,
        {
            headers: {
                accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token?.access_token}`,
            },
        }
    );

    return response;
}

export function* checkTenantExist(action: ActionType<typeof actions.checkOrCreateTenant.request>) {
    const { token } = yield select((state: ReduxState) => state.aiAgent);
    const { i_customer, email } = yield select((state: ReduxState) => state.generic.sessionData);
    const customerEmail: string | null = yield select(
        (state: ReduxState) => state.generic.globalCustomerInfo?.customer_info?.email || null
    );

    try {
        let tenantExists = false;
        let existingTenantResponse: AxiosResponse<TenantResponse> | null = null;

        // Check if tenant exists
        try {
            const response: AxiosResponse<TenantResponse> = yield axios.get(
                `${Config.VOICE_AI_ADAPTER_URL}/tenants/${i_customer}`,
                {
                    headers: {
                        accept: 'application/json',
                        Authorization: `Bearer ${token?.access_token}`,
                    },
                }
            );

            // Fix: Tenant exists if tenant_id EQUALS i_customer (was inverted with !==)
            tenantExists = response.data.tenant_id === i_customer;
            existingTenantResponse = response;
        } catch (e) {
            // Tenant doesn't exist - this is expected in some cases
            tenantExists = false;
        }

        // Create tenant if it doesn't exist and creation is requested
        if (action.payload.createIfNotExists && !tenantExists && (email || customerEmail) && token?.access_token) {
            const response = yield* createTenant();

            yield put(
                actions.checkOrCreateTenant.success({
                    ...response.data,
                    status: response.data.status === 'success' ? 'existing' : 'not_existing',
                })
            );
        } else if (existingTenantResponse) {
            // Tenant already exists
            yield put(actions.checkOrCreateTenant.success(existingTenantResponse.data));
        } else {
            // Tenant doesn't exist and we're not creating it
            yield put(
                actions.checkOrCreateTenant.success({
                    status: 'not_existing',
                    tenant_id: undefined,
                    message: 'Tenant does not exist',
                })
            );
        }
    } catch (e: any) {
        yield put(actions.checkOrCreateTenant.failure(e));
    }
}

export function* checkTenant() {
    const { token } = yield select((state: ReduxState) => state.aiAgent);
    const { i_customer, email } = yield select((state: ReduxState) => state.generic.sessionData);
    const customerEmail: string | null = yield select(
        (state: ReduxState) => state.generic.globalCustomerInfo?.customer_info?.email || null
    );

    try {
        const responseTenantExist: AxiosResponse<TenantResponse> = yield axios.get(
            `${Config.VOICE_AI_ADAPTER_URL}/tenants/${i_customer}`,
            {
                headers: {
                    accept: 'application/json',
                    Authorization: `Bearer ${token?.access_token}`,
                },
            }
        );

        // If tenant exists, call /tenants endpoint to get the existing token
        if (
            responseTenantExist.data.tenant_id === i_customer &&
            (email || customerEmail) &&
            token?.access_token
        ) {
            // Tenant exists - get the token by calling POST /tenants (returns existing token, doesn't create)
            const response = yield* createTenant();

            yield put(
                actions.checkTenant.success({
                    ...response.data,
                    status: response.data.status === 'success' ? 'existing' : 'not_existing',
                })
            );
        } else {
            // Tenant doesn't exist
            yield put(
                actions.checkTenant.success({
                    status: 'not_existing',
                    tenant_id: undefined,
                    message: 'Tenant does not exist',
                })
            );
        }
    } catch (e: any) {
        // Tenant doesn't exist or other error
        yield put(
            actions.checkTenant.success({
                status: 'not_existing',
                tenant_id: undefined,
                message: e?.response?.data?.message || 'Tenant does not exist',
            })
        );
    }
}

export function* getAiAgentsList() {

    const {token} = yield select((state: ReduxState) => state.aiAgent);

    try {

        const response: AxiosResponse<AiAgentsResponse> = yield axios.get(
            Config.VOICE_AI_ADAPTER_URL + '/voice-ai-agents',
            {
                headers: {
                    'accept': 'application/json',
                    'Authorization': `Bearer ${token?.access_token}`,
                },
            }
        );

        yield put(actions.getAiAgentsList.success(response.data));

    } catch (e: any) {
        yield put(actions.getAiAgentsList.failure({error:e?.response?.data?.details || 'unknow'}));

    }

}


export function* getAccessLink() {

    const {token} = yield select((state: ReduxState) => state.aiAgent);

    try {

        const response: AxiosResponse<SsoResponse> = yield axios.get(
            Config.VOICE_AI_ADAPTER_URL + '/auth/sso-auth-token',
            {
                headers: {
                    'accept': 'application/json',
                    'Authorization': `Bearer ${token?.access_token}`,
                },
            }
        );

        yield put(actions.getAccessLink.success(response.data));

    } catch (e: any) {
        yield put(actions.getAccessLink.failure(e.response?.data || e.message || e.toString()));

    }

}


export const aiAgentsSaga = [
    takeLatest(actions.exchangeTokenWithInternalAdapter.request, exchangeTokenWithInternalAdapter),
    takeLatest(actions.checkOrCreateTenant.request, checkTenantExist),
    takeLatest(actions.checkTenant.request, checkTenant),
    takeLatest(actions.getAiAgentsList.request, getAiAgentsList),
    takeLatest(actions.getAccessLink.request, getAccessLink),

];
